// `SessionKind` — the hard enum for what a conversation IS.
//
// THE DEFINITION every rule below falls out of: **kind names what REOPENING
// this row gives you.** It is the stored, cheap twin of the client's
// `PickTarget` — a discovery list must answer "what is this?" from one indexed
// column, without decrypting a transcript or folding an event log per row.
//
//   - `unknown`  — no user turn yet (a pre-warmed session the user abandoned),
//                  or a row a server wrote without the column.
//   - `chat`     — free-form conversation, bound to no issue.
//   - `issue`    — issue work: creating or refining one. Both are the same
//                  value because they are the same code path (the create wizard
//                  ENDS in the refine surface) and no consumer splits them.
//   - `implement` — a dedicated autonomous implementation run, which the server
//                  itself opens with an explicit intent.
//
// MONOTONIC by rank (the array order). A promotion is a one-way move up:
// `promoteKind` never demotes, so a conversation that once did issue work can
// never fall back to reading `chat`. That direction is the safe one — the Focus
// history rail lists `kind = 'chat'` and withholds issue rows, so a wrong `chat`
// SHOWS a row whose reopen is broken, while a wrong `issue` merely hides one.
// Monotonicity is what makes every unreachable case fail toward hidden.
//
// Crypto-free and lookbehind-free: this module ships to the browser through the
// `@loom/shared/session` barrel, and Safari 14 throws on a regex lookbehind.

import { z } from "zod";

/**
 * Every kind, in RANK ORDER — the index in this array is the rank, and
 * `promoteKind` / `kindsBelow` are the only readers of that ordering. A new
 * value goes in at its rank here and nowhere else; the migration's CHECK
 * constraint is held to this list by
 * `apps/server/test/arch/sessions_index_kind_matches_migration.test.ts`.
 */
export const SESSION_KIND_VALUES = [
  "unknown",
  "chat",
  "issue",
  "implement",
] as const;

export type SessionKind = (typeof SESSION_KIND_VALUES)[number];

export const SessionKindSchema = z.enum(SESSION_KIND_VALUES);

export const isSessionKind = (v: unknown): v is SessionKind =>
  typeof v === "string" &&
  (SESSION_KIND_VALUES as ReadonlyArray<string>).includes(v);

const rankOf = (k: SessionKind): number => SESSION_KIND_VALUES.indexOf(k);

/**
 * The higher-ranked of the two. The whole promotion rule: a kind moves up and
 * never down, so repeating a promotion is a no-op and a later, lower-ranked
 * observation cannot walk an established kind back.
 */
export const promoteKind = (prev: SessionKind, next: SessionKind): SessionKind =>
  rankOf(next) > rankOf(prev) ? next : prev;

/**
 * Every value strictly below `k` — the SQL `IN`-list of the monotonic
 * `UPDATE … WHERE kind IN (…)`. Derived rather than hand-written so the store's
 * predicate and this enum cannot drift.
 */
export const kindsBelow = (k: SessionKind): ReadonlyArray<SessionKind> =>
  SESSION_KIND_VALUES.slice(0, rankOf(k));
